import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { client, paypalCheckout, dataCollector } from 'braintree-web';
import { connect } from 'react-redux';
import styled from '@emotion/styled';
import { toast } from 'react-toastify';
import Router from 'next/router';
import * as Sentry from '@sentry/node';

import Button from '../../atoms/button';
import Typography from '../../atoms/typography';

import { updateCart } from '../../store/modules/cart/actions';
import { createBraintreeTransaction } from '../../pages/api';
import trackEvent from '../../utils/tracking';
import { logAmplitude } from '../../utils/amplitude';
import { useSiteSpect } from '../../utils/context/sitespect-provider';

const PayPalWrapper = styled.div`
  position: relative;
  min-height: 4.5rem;
  overflow: hidden;
  margin-top: ${(props) => props.theme.modularScale.small};
`;

const NewCheckoutWrapper = styled.div`
  position: relative;
  display: flex;
  background-color: ${(props) => props.theme.color.paypalYellow};
  border: 1px solid ${(props) => props.theme.color.paypalYellow};

  :hover {
    background-color: ${(props) => props.theme.color.paypalYellowHover};
    transition: background-color ${(props) => props.theme.animation.default} ${(props) => props.theme.animation.easeOutQuad};
  }
`;

const NewCheckoutPayPal = styled.div`
  flex: 1 1 auto;
  height: 4rem;
`;

const Copy = styled(Typography)`
  color: ${(props) => props.theme.color.brand};
`;

const ReviewPaymentButton = styled(Button)`
  width: 100%;
  display: ${({ isVisible }) => (isVisible ? 'block' : 'none')};
`;

const PayPal = styled.div`
  // This sits the PayPal div on top of the Review Payment button.
  // The PayPal div will not be visible, but will be clickable.
  position: absolute;
  top: 0;
  width: 100%;
  opacity: 0.01;
  z-index: ${({ theme }) => theme.layers.balcony};
`;

const ButtonWrapper = styled.div`
  position: relative;
  min-height: 4.5rem;
  margin-top: ${(props) => props.theme.modularScale.medium};
`;

const Loading = styled.span`
  opacity: 0.75;
  z-index: ${(props) => props.theme.layers.box};

  ${(props) => props.theme.loader(3, props.theme.color.paypalYellow)}
`;

const BraintreePayPal = (props) => {
  const [loading, setLoading] = useState(true);
  const [payPalError, setPayPalError] = useState(false);
  const [payment] = useState(props.cart.payment_methods.find((m) => m.name === 'Braintree'));

  const { checkout_revamp = false } = useSiteSpect();

  let clientInstance = null;
  let deviceData = null;

  useEffect(() => {
    client.create({
      authorization: props.token
    }).then(async (instance) => {
      clientInstance = instance;

      return dataCollector.create({
        client: instance,
        kount: true,
        paypal: true
      });
    }).then((dataCollectorInstance) => {
      deviceData = dataCollectorInstance.deviceData;

      return paypalCheckout.create({
        client: clientInstance
      });
    }).then((paypalCheckoutInstance) => {
      if (Object.prototype.hasOwnProperty.call(global, 'PayPalSDK')) {
        return global.PayPalSDK.Buttons({
          style: {
            height: 42,
            color: 'gold'
          },
          // fundingSource renders specific buttons
          fundingSource: global.PayPalSDK.FUNDING[props.fundingSource],
          createOrder: () => paypalCheckoutInstance.createPayment({
            flow: 'checkout',
            currency: 'USD',
            amount: props.cart.subtotals.order_total,
            intent: 'capture',
            enableShippingAddress: false
          }),

          onApprove(data) {
            trackEvent({
              eventCategory: 'Payment',
              eventAction: 'Add PayPal payment',
              eventLabel: 'Attempt'
            });

            logAmplitude('Submitted Checkout Payment', { cart: props.cart, method: 'PayPal' });

            return paypalCheckoutInstance.tokenizePayment(data)
              // some logic here before tokenization happens below
              .then(async (order) => {
                // Submit payload.nonce to your server
                const request = {
                  order_id: props.cart.number,
                  payment_method_id: payment.id,
                  state: 'payment',
                  options: {
                    restart_checkout: false
                  },
                  transaction: {
                    device_data: deviceData,
                    email: order.details.email,
                    nonce: order.nonce,
                    phone: order.details.phone,
                    payment_type: 'PayPalAccount'
                  }
                };

                await createBraintreeTransaction({ body: request, order_token: props.cart.token })
                  .then((cart) => {
                    const cartData = cart?.data ?? cart;

                    if (cartData.errors && Array.isArray(cartData.errors)) {
                      cartData.errors.forEach((error) => {
                        if (error.message && typeof error.message === 'string') {
                          toast(error.message, { type: toast.TYPE.ERROR, autoClose: false });
                        }

                        if (error.code === 422) {
                          props.updateCart({
                            state: 'address'
                          });
                        }
                      });
                    } else {
                      props.updateCart({
                        ...cartData,
                        state: 'confirm'
                      });

                      trackEvent({
                        eventCategory: 'Payment',
                        eventAction: 'Add PayPal payment',
                        eventLabel: 'Success'
                      });

                      const redirect = checkout_revamp ? '/checkout-new' : '/checkout';

                      Router.push({
                        pathname: redirect,
                        query: { paypal: 'true' }
                      }, redirect).then(() => {
                        if (global.window.location.pathname === redirect) {
                          trackEvent({
                            eventCategory: 'Cart',
                            eventAction: 'Submit cart',
                            eventLabel: 'Success'
                          });
                        }
                        global.window.scrollTo(0, 0);
                      });
                    }
                  });
              });
          },

          onCancel(data) {
            trackEvent({
              eventCategory: 'Payment',
              eventAction: 'Add PayPal payment',
              eventLabel: 'Cancelled'
            });

            console.error('PayPal SDK checkout payment cancelled', JSON.stringify(data, 0, 2));
          },

          onError(err) {
            trackEvent({
              eventCategory: 'Payment',
              eventAction: 'Add PayPal payment',
              eventLabel: 'Failure'
            });

            Sentry.withScope((scope) => {
              scope.setContext('paypal instance', paypalCheckoutInstance);
              Sentry.captureException(err);
            });
          }
        }).render(
          `#${props.id}`
        );
      }
      return Promise.resolve();
    })
      .then(() => {
        setLoading(false);
      })
      .catch((error) => {
        setPayPalError(true);
        setLoading(false);
        Sentry.captureException(error);
      });
  }, []);

  return (
    <>
      {
        !props.newCheckout ? (
          <PayPalWrapper>
            <Copy like="dec-1" element="p">
                You’ll be prompted to log in with your selected payment option when you
                click on ‘Review Payment’. Please follow the instructions, and you’ll be
                able to complete your purchase.
            </Copy>
            <ButtonWrapper>
              {loading && <Loading />}
              <PayPal id={props.id} />
              <ReviewPaymentButton
                isVisible={!loading}
                aria-hidden="true"
                disabled={payPalError}
              >
                  REVIEW PAYMENT
              </ReviewPaymentButton>
            </ButtonWrapper>
          </PayPalWrapper>
        ) : (
          <NewCheckoutWrapper>
            {loading && <Loading />}
            <NewCheckoutPayPal id={props.id} />
          </NewCheckoutWrapper>
        )
      }
    </>
  );
};

BraintreePayPal.defaultProps = {
  newCheckout: false
};

BraintreePayPal.propTypes = {
  id: PropTypes.string.isRequired,
  cart: PropTypes.object.isRequired,
  token: PropTypes.string.isRequired,
  updateCart: PropTypes.func.isRequired,
  fundingSource: PropTypes.string.isRequired,
  newCheckout: PropTypes.bool
};

const mapStateToProps = (state) => ({
  cart: state.cart
});

const mapDispatchToProps = (dispatch) => ({
  updateCart: (cart) => dispatch(updateCart(cart))
});

const ConnectedBraintreePayPal = connect(
  mapStateToProps, mapDispatchToProps
)(BraintreePayPal);

ConnectedBraintreePayPal.displayName = 'BraintreePayPal';
ConnectedBraintreePayPal.whyDidYouRender = true;

export default ConnectedBraintreePayPal;
